import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIndicatorIcon,
  AddCircle as AddCircleIcon,
  Folder as FolderIcon,
  TextFields as TextFieldsIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_FORM, UPDATE_FORM, GET_FORM } from "../../graphql/operations";
import {
  FormFieldInput,
  FieldType,
  FIELD_TYPES,
  FORM_TYPES,
  FormType,
  // FormStatus, // Added
} from "../../graphql/operations";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";

// Safe fallback for FIELD_TYPES if it's undefined
const SAFE_FIELD_TYPES = FIELD_TYPES || {
  TEXT: { label: "Text Input", icon: "📝" },
  NUMBER: { label: "Number Input", icon: "🔢" },
  EMAIL: { label: "Email Input", icon: "📧" },
  SELECT: { label: "Dropdown", icon: "▼" },
  MULTISELECT: { label: "Multi-Select", icon: "☑️" },
  DATE: { label: "Date Picker", icon: "📅" },
  BOOLEAN: { label: "Checkbox", icon: "✓" },
};

const SAFE_FORM_TYPES = FORM_TYPES || {
  EMPLOYEE: { label: "Employee Form", icon: "👥" },
  LOCATION: { label: "Location Form", icon: "📍" },
  COMPLIANCE: { label: "Compliance Form", icon: "📋" },
};

// Interface for Form Section
interface FormSection {
  id: string;
  title: string;
  subtitle?: string;
  order: number;
  fields: FormFieldInput[];
}

// FormKey interface for mutation response
interface FormKey {
  pk: string;
  sk: string;
}

// Extended FormInput with sections
interface FormWithSectionsInput {
  title: string;
  formType: FormType;
  description?: string;
  version: number;
  sections: FormSection[];
  // status: FormStatus;
}

const CreateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [openSectionDialog, setOpenSectionDialog] = useState(false);
  const [openFieldDialog, setOpenFieldDialog] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(
    null
  );
  const [editingFieldIndex, setEditingFieldIndex] = useState<{
    sectionIndex: number;
    fieldIndex: number;
  } | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [currentSectionId, setCurrentSectionId] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormWithSectionsInput>({
    title: "",
    formType: FormType.EMPLOYEE,
    description: "",
    sections: [],
    version: isEditMode ? 0 : 1,
    // status: FormStatus.DRAFT,
  });

  const [currentField, setCurrentField] = useState<FormFieldInput>({
    type: FieldType.TEXT,
    label: "",
    required: false,
    options: [],
    validation: {},
  });

  const [currentSection, setCurrentSection] = useState<
    Omit<FormSection, "id" | "order" | "fields">
  >({
    title: "",
    subtitle: "",
  });

  // Fetch existing form data if in edit mode
  const {
    data: existingFormData,
    loading: loadingForm,
    error: fetchError,
  } = useQuery(GET_FORM, {
    variables: { id },
    skip: !isEditMode,
  });

  const [createForm, { loading: creating, error: createError }] = useMutation<{
    createForm: FormKey[];
  }>(CREATE_FORM);
  const [updateForm, { loading: updating, error: updateError }] = useMutation<{
    updateForm: FormKey[];
  }>(UPDATE_FORM);

  const loading = creating || updating || loadingForm;

  // Populate form data when existing form is loaded
  useEffect(() => {
    if (existingFormData?.getForm) {
      const form = existingFormData.getForm;

      // Check if form has sections or legacy fields
      if (form.sections && form.sections.length > 0) {
        // Form already has sections
        setFormData({
          title: form.title,
          formType: form.formType,
          description: form.description || "",
          version: form.version,
          // status: form.status,
          sections: form.sections.map((section: any, index: number) => ({
            id: section.id || `section-${index}`,
            title: section.title,
            subtitle: section.subtitle || "",
            order: section.order || index,
            fields: section.fields.map((field: any) => ({
              type: field.type,
              label: field.label,
              required: field.required || false,
              options: field.options || [],
              validation: field.validation || {},
            })),
          })),
        });
      } else if (form.fields && form.fields.length > 0) {
        // Legacy form - convert fields to a single section
        setFormData({
          title: form.title,
          formType: form.formType,
          description: form.description || "",
          version: form.version,
          // status: form.status,
          sections: [
            {
              id: "section-0",
              title: "Form Fields",
              subtitle: "All form fields",
              order: 0,
              fields: form.fields.map((field: any) => ({
                type: field.type,
                label: field.label,
                required: field.required || false,
                options: field.options || [],
                validation: field.validation || {},
              })),
            },
          ],
        });
      } else {
        // Empty form
        setFormData({
          title: form.title,
          formType: form.formType,
          description: form.description || "",
          version: form.version,
          // status: form.status,
          sections: [],
        });
      }
    }
  }, [existingFormData]);

  // Safe field type getter
  const getFieldTypeInfo = (fieldType: string) => {
    return (
      SAFE_FIELD_TYPES[fieldType as FieldType] || {
        label: fieldType,
        icon: "❓",
      }
    );
  };

  const removeTypename = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(removeTypename);
    } else if (obj !== null && typeof obj === "object") {
      const newObj: any = {};
      for (const key in obj) {
        if (key !== "__typename") {
          newObj[key] = removeTypename(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validate required fields
    if (!formData.title.trim()) {
      setFormError("Form title is required");
      return;
    }

    if (!formData.formType) {
      setFormError("Form type is required");
      return;
    }

    if (formData.sections.length === 0) {
      setFormError("At least one section is required");
      return;
    }

    const totalFields = formData.sections.reduce(
      (total, section) => total + section.fields.length,
      0
    );

    if (totalFields === 0) {
      setFormError("At least one field is required");
      return;
    }

    try {
      // Clean the sections - remove id field since it's not in FormSectionInput
      const cleanedSections = formData.sections.map((section) => ({
        id: section.id,
        title: section.title,
        subtitle: section.subtitle || undefined, // Convert empty string to undefined
        order: section.order,
        fields: section.fields.map((field) => ({
          type: field.type,
          label: field.label,
          required: field.required || false,
          options:
            field.options && field.options.length > 0
              ? field.options
              : undefined,
          validation:
            field.validation && Object.keys(field.validation).length > 0
              ? removeTypename(field.validation)
              : undefined,
        })),
      }));

      // Create the input according to FormInput type
      const cleanedFormData = {
        title: formData.title.trim(),
        formType: formData.formType,
        description: formData.description?.trim() || undefined,
        version: formData.version,
        sections: cleanedSections,
      };

      console.log(
        "Sending form data:",
        JSON.stringify(cleanedFormData, null, 2)
      );

      if (isEditMode && id) {
        const result = await updateForm({
          variables: {
            id,
            input: cleanedFormData,
          },
        });
        console.log("Form updated:", result.data?.updateForm);
      } else {
        const result = await createForm({
          variables: {
            input: cleanedFormData,
          },
        });
        console.log("Form created:", result.data?.createForm);
      }

      navigate("/dashboard/forms");
    } catch (err) {
      console.error("Error saving form:", err);
      setFormError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  // Section Management Functions (same as before)
  const addOrUpdateSection = () => {
    if (editingSectionIndex !== null) {
      const updatedSections = [...formData.sections];
      updatedSections[editingSectionIndex] = {
        ...updatedSections[editingSectionIndex],
        ...currentSection,
      };
      setFormData({ ...formData, sections: updatedSections });
    } else {
      const newSection: FormSection = {
        id: `section-${Date.now()}`,
        title: currentSection.title,
        subtitle: currentSection.subtitle || "",
        order: formData.sections.length,
        fields: [],
      };
      setFormData({
        ...formData,
        sections: [...formData.sections, newSection],
      });
      setCurrentSectionId(newSection.id);
    }
    setOpenSectionDialog(false);
    setCurrentSection({ title: "", subtitle: "" });
    setEditingSectionIndex(null);
  };

  const editSection = (index: number) => {
    setCurrentSection({
      title: formData.sections[index].title,
      subtitle: formData.sections[index].subtitle || "",
    });
    setEditingSectionIndex(index);
    setOpenSectionDialog(true);
  };

  // const removeSection = (index: number) => {
  //   const updatedSections = formData.sections.filter((_, i) => i !== index);
  //   const reorderedSections = updatedSections.map((section, idx) => ({
  //     ...section,
  //     order: idx,
  //   }));
  //   setFormData({ ...formData, sections: reorderedSections });
  // };

  // Field Management Functions (same as before)
  const addOrUpdateField = () => {
    if (editingFieldIndex !== null) {
      const updatedSections = [...formData.sections];
      updatedSections[editingFieldIndex.sectionIndex].fields[
        editingFieldIndex.fieldIndex
      ] = currentField;
      setFormData({ ...formData, sections: updatedSections });
    } else if (currentSectionId) {
      const sectionIndex = formData.sections.findIndex(
        (section) => section.id === currentSectionId
      );
      if (sectionIndex !== -1) {
        const updatedSections = [...formData.sections];
        updatedSections[sectionIndex].fields.push(currentField);
        setFormData({ ...formData, sections: updatedSections });
      }
    }
    setOpenFieldDialog(false);
    setCurrentField({
      type: FieldType.TEXT,
      label: "",
      required: false,
      options: [],
      validation: {},
    });
    setEditingFieldIndex(null);
  };

  const editField = (sectionIndex: number, fieldIndex: number) => {
    setCurrentField(formData.sections[sectionIndex].fields[fieldIndex]);
    setEditingFieldIndex({ sectionIndex, fieldIndex });
    setCurrentSectionId(formData.sections[sectionIndex].id);
    setOpenFieldDialog(true);
  };

  const removeField = (sectionIndex: number, fieldIndex: number) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].fields = updatedSections[
      sectionIndex
    ].fields.filter((_, i) => i !== fieldIndex);
    setFormData({ ...formData, sections: updatedSections });
  };

  // Field dialog helpers
  const addOption = () => {
    setCurrentField({
      ...currentField,
      options: [...(currentField.options || []), ""],
    });
  };

  const updateOption = (index: number, value: string) => {
    const updatedOptions = [...(currentField.options || [])];
    updatedOptions[index] = value;
    setCurrentField({
      ...currentField,
      options: updatedOptions,
    });
  };

  const removeOption = (index: number) => {
    const updatedOptions = (currentField.options || []).filter(
      (_, i) => i !== index
    );
    setCurrentField({
      ...currentField,
      options: updatedOptions.length > 0 ? updatedOptions : undefined,
    });
  };

  const error = createError || updateError || fetchError;

  if (fetchError) {
    return (
      <Box sx={{ maxWidth: 800, margin: "0 auto", p: 3 }}>
        <Alert severity="error">Error loading form: {fetchError.message}</Alert>
        <Button onClick={() => navigate("/dashboard/forms")} sx={{ mt: 2 }}>
          Back to Forms
        </Button>
      </Box>
    );
  }

  const totalFields = formData.sections.reduce(
    (total, section) => total + section.fields.length,
    0
  );

  return (
    <Box sx={{ maxWidth: 800, margin: "0 auto", p: 3 }}>
      <Helmet>
        <title>
          Almond Tree Dashboard - {isEditMode ? "Edit" : "Create"} Form
        </title>
      </Helmet>

      <Typography variant="h4" gutterBottom>
        {isEditMode ? "Edit Form" : "Create Form"}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Form Type (Read-only in edit mode) */}
            {isEditMode ? (
              <FormControl fullWidth>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Form Type (Cannot be changed)
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    backgroundColor: "action.hover",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body1">
                    {SAFE_FORM_TYPES[formData.formType]?.label ||
                      formData.formType}
                  </Typography>
                </Paper>
              </FormControl>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Form Type</InputLabel>
                <Select
                  value={formData.formType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      formType: e.target.value as FormType,
                    })
                  }
                  label="Form Type"
                >
                  {Object.entries(SAFE_FORM_TYPES).map(
                    ([value, { label, icon }]) => (
                      <MenuItem key={value} value={value}>
                        {icon} {label}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            )}

            {/* Form Title (Read-only in edit mode) */}
            {isEditMode ? (
              <FormControl fullWidth>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Form Title (Cannot be changed)
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    backgroundColor: "action.hover",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body1">{formData.title}</Typography>
                </Paper>
              </FormControl>
            ) : (
              <TextField
                label="Form Title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
                fullWidth
                error={!!formError && !formData.title.trim()}
              />
            )}

            {/* Form Description */}
            <TextField
              label="Form Description (Optional)"
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={2}
              fullWidth
            />

            {/* Form Status */}
            {/* <FormControl fullWidth>
              <InputLabel>Form Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as FormStatus,
                  })
                }
                label="Form Status"
              >
                <MenuItem value={FormStatus.DRAFT}>Draft</MenuItem>
                <MenuItem value={FormStatus.ACTIVE}>Active</MenuItem>
                <MenuItem value={FormStatus.ARCHIVED}>Archived</MenuItem>
              </Select>
            </FormControl> */}

            <Divider>
              <Typography variant="h6" color="textSecondary">
                Form Structure
              </Typography>
            </Divider>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={activeTab}
                onChange={(_e, newValue) => setActiveTab(newValue)}
              >
                <Tab
                  label={`Sections (${formData.sections.length})`}
                  icon={<FolderIcon />}
                  iconPosition="start"
                />
                <Tab
                  label={`Fields (${totalFields})`}
                  icon={<TextFieldsIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {/* Sections Tab */}
            {activeTab === 0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    Organize your form into sections
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenSectionDialog(true)}
                  >
                    Add Section
                  </Button>
                </Box>

                {formData.sections.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No sections added yet. Sections help organize your form
                    fields into logical groups.
                  </Alert>
                ) : (
                  <List>
                    {formData.sections.map((section, sectionIndex) => (
                      <Accordion key={section.id} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <DragIndicatorIcon
                              sx={{ mr: 1, color: "action.active" }}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1">
                                {section.title}
                              </Typography>
                              {section.subtitle && (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {section.subtitle}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {section.fields.length} field(s)
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {section.fields.map((field, fieldIndex) => {
                              const fieldTypeInfo = getFieldTypeInfo(
                                field.type
                              );
                              return (
                                <ListItem key={fieldIndex} divider>
                                  <ListItemText
                                    primary={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <TextFieldsIcon
                                          sx={{ mr: 1, fontSize: 16 }}
                                        />
                                        {field.label} {field.required && "*"}
                                      </Box>
                                    }
                                    secondary={
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {fieldTypeInfo.label}
                                      </Typography>
                                    }
                                  />
                                  <ListItemSecondaryAction>
                                    <Tooltip title="Edit field">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          editField(sectionIndex, fieldIndex)
                                        }
                                        sx={{ mr: 0.5 }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete field">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          removeField(sectionIndex, fieldIndex)
                                        }
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              );
                            })}
                            {section.fields.length === 0 && (
                              <ListItem>
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      No fields in this section
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            )}
                          </List>
                          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                setCurrentSectionId(section.id);
                                setOpenFieldDialog(true);
                              }}
                            >
                              Add Field
                            </Button>
                            <Button
                              size="small"
                              onClick={() => editSection(sectionIndex)}
                            >
                              Edit Section
                            </Button>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </List>
                )}

                {formData.sections.length > 0 && (
                  <Box
                    sx={{ mt: 3, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<AddCircleIcon />}
                      onClick={() => setOpenSectionDialog(true)}
                    >
                      Add Another Section
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Fields Tab (All Fields View) */}
            {activeTab === 1 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    All Fields ({totalFields})
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenFieldDialog(true)}
                    disabled={formData.sections.length === 0}
                  >
                    Add Field
                  </Button>
                </Box>

                {totalFields === 0 ? (
                  <Alert severity="warning">
                    No fields added yet.{" "}
                    {formData.sections.length === 0
                      ? "Add a section first, then add fields to it."
                      : "Select a section from the Sections tab and add fields."}
                  </Alert>
                ) : (
                  <List>
                    {formData.sections.map((section, sectionIndex) => (
                      <React.Fragment key={section.id}>
                        {section.fields.length > 0 && (
                          <>
                            <ListItem
                              divider
                              sx={{ backgroundColor: "action.hover" }}
                            >
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle2">
                                    {section.title}
                                    {section.subtitle && (
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{ ml: 1 }}
                                      >
                                        ({section.subtitle})
                                      </Typography>
                                    )}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            {section.fields.map((field, fieldIndex) => {
                              const fieldTypeInfo = getFieldTypeInfo(
                                field.type
                              );
                              return (
                                <ListItem
                                  key={`${sectionIndex}-${fieldIndex}`}
                                  divider
                                >
                                  <ListItemText
                                    primary={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        {field.label} {field.required && "*"}
                                      </Box>
                                    }
                                    secondary={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Chip
                                          label={fieldTypeInfo.label}
                                          size="small"
                                          variant="outlined"
                                        />
                                        <Typography
                                          variant="caption"
                                          color="textSecondary"
                                        >
                                          Section: {section.title}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                  <ListItemSecondaryAction>
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          editField(sectionIndex, fieldIndex)
                                        }
                                        sx={{ mr: 0.5 }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          removeField(sectionIndex, fieldIndex)
                                        }
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              );
                            })}
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* Error Display */}
            {(error || formError) && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formError || error?.message}
              </Alert>
            )}

            {/* Validation Summary */}
            {formData.sections.length === 0 && (
              <Alert severity="warning">
                At least one section is required before creating the form.
              </Alert>
            )}

            {totalFields === 0 && formData.sections.length > 0 && (
              <Alert severity="warning">
                At least one field is required before creating the form.
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={
                  loading || totalFields === 0 || !formData.title.trim()
                }
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : isEditMode
                  ? "Update Form"
                  : "Create Form"}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/dashboard/forms")}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>

      {/* Section Configuration Dialog */}
      <Dialog
        open={openSectionDialog}
        onClose={() => setOpenSectionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSectionIndex !== null ? "Edit Section" : "Add New Section"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Section Title"
              value={currentSection.title}
              onChange={(e) =>
                setCurrentSection({ ...currentSection, title: e.target.value })
              }
              required
              fullWidth
              helperText="A descriptive title for this section"
            />
            <TextField
              label="Section Subtitle (Optional)"
              value={currentSection.subtitle || ""}
              onChange={(e) =>
                setCurrentSection({
                  ...currentSection,
                  subtitle: e.target.value,
                })
              }
              fullWidth
              multiline
              rows={2}
              helperText="Additional description or instructions for this section"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSectionDialog(false)}>Cancel</Button>
          <Button
            onClick={addOrUpdateSection}
            variant="contained"
            disabled={!currentSection.title}
          >
            {editingSectionIndex !== null ? "Update Section" : "Add Section"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field Configuration Dialog */}
      <Dialog
        open={openFieldDialog}
        onClose={() => setOpenFieldDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingFieldIndex !== null ? "Edit Field" : "Add New Field"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Section Selection (only when adding new field) */}
            {editingFieldIndex === null && formData.sections.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Select Section</InputLabel>
                <Select
                  value={currentSectionId}
                  onChange={(e) => setCurrentSectionId(e.target.value)}
                  label="Select Section"
                >
                  {formData.sections.map((section) => (
                    <MenuItem key={section.id} value={section.id}>
                      {section.title}
                    </MenuItem>
                  ))}
                </Select>
                {!currentSectionId && (
                  <Typography variant="caption" color="error">
                    Please select a section for this field
                  </Typography>
                )}
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Field Type</InputLabel>
              <Select
                value={currentField.type}
                onChange={(e) =>
                  setCurrentField({
                    ...currentField,
                    type: e.target.value as FieldType,
                  })
                }
                label="Field Type"
              >
                {Object.entries(SAFE_FIELD_TYPES).map(
                  ([value, { label, icon }]) => (
                    <MenuItem key={value} value={value}>
                      {icon} {label}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            <TextField
              label="Field Label"
              value={currentField.label}
              onChange={(e) =>
                setCurrentField({ ...currentField, label: e.target.value })
              }
              required
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={currentField.required}
                  onChange={(e) =>
                    setCurrentField({
                      ...currentField,
                      required: e.target.checked,
                    })
                  }
                />
              }
              label="Required field"
            />

            {(currentField.type === FieldType.SELECT ||
              currentField.type === FieldType.MULTISELECT) && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Options
                </Typography>
                <Stack spacing={1}>
                  {currentField.options?.map((option, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", gap: 1, alignItems: "center" }}
                    >
                      <TextField
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        fullWidth
                        size="small"
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeOption(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addOption}
                    size="small"
                  >
                    Add Option
                  </Button>
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFieldDialog(false)}>Cancel</Button>
          <Button
            onClick={addOrUpdateField}
            variant="contained"
            disabled={
              !currentField.label ||
              (editingFieldIndex === null && !currentSectionId)
            }
          >
            {editingFieldIndex !== null ? "Update Field" : "Add Field"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateForm;
